import { EnumAccountsPackageType } from "@/app/services/generated/enums/enumAccountsPackageType.generated";
import type { EnumEcommerceOrderOriginType } from "@/app/services/generated/enums/enumEcommerceOrderOriginType.generated";
import Guid from "@/app/types/common/guid";
import { MenuItem } from "@/app/ui/navigation/types";
import type { Location } from "vue-router";
import { EnumUserPermission } from "@/app/services/generated/enums/enumUserPermission.generated";
import { hasPermissionsUsingUserStore } from "@/components/pages/administration/user_roles/UserAccessHandler";
import messages from "@/plugins/i18n";
import { EnumIntegrationType } from "@/app/services/generated/enums/enumIntegrationType.generated";
import { bigCommerceTabs } from "@/components/pages/integrations/bigCommerce/integrationsBigCommerce";
import IntegrationsMenuItem from "@/components/pages/integrations/IntegrationsMenuItem.vue";
import { RouteBuilder } from "@/app/ui/navigation/routeBuilder";

const builder = new RouteBuilder("Integrations_");

export const integrationRoutes = {
  whatsapp: builder.route({
    url: "/integrations/whatsapp",
    view: (): Promise<any> =>
      import(
        /* webpackChunkName: "integrations" */ "@/components/pages/integrations/whatsapp/WhatsApp.vue"
      ),
  }),
};

export const integrationsMenu = {
  root: "navigation_integrations_group",
  rootViewLocation: (): Location => ({
    name: integrationsMenu.root,
  }),
  list: "integrations_list",
  brewmanTradePortal: {
    view: "brewman_trade_portal",
    viewLocation: (): Location => ({
      name: integrationsMenu.brewmanTradePortal.view,
    }),
  },
  commerce7: {
    view: "commerce7",
    viewLocation: (): Location => ({
      name: integrationsMenu.commerce7.view,
    }),
  },
  ptvRo: {
    view: "ptv_ro",
    viewLocation: (): Location => ({
      name: integrationsMenu.ptvRo.view,
    }),
  },
  xero: "xero",
  fortnox: "fortnox",
  square: {
    view: "square",
    squareViewLocation: (id: Guid): Location => ({
      name: integrationsMenu.square.view,
      params: {
        id: id.toString(),
      },
    }),
    create: "square_create",
    squareCreateLocation: (): Location => ({
      name: integrationsMenu.square.create,
    }),
    callback: "square_callback",
  },
  quickBooks: "quickBooks",
  sageOne: "sage_one",
  sageOneZa: "sage_one_za",
  sage50: "sage_50",
  sage200: "sage_200",
  brewmanApi: "brewman_api",
  noAccountsPackage: "no_accounts_package",
  sellar: {
    view: "sellar",
    viewLocation: (id: Guid): Location => ({
      name: integrationsMenu.sellar.view,
      params: {
        id: id.toString(),
      },
    }),
    create: "sellar_create",
    createLocation: (): Location => ({
      name: integrationsMenu.sellar.create,
    }),
  },
  zettle: "zettle",
  beerflex: "beerflex",
  greentree: {
    view: "greentree_navigation",
    viewLocation: (): Location => ({
      name: integrationsMenu.greentree.view,
    }),
    edit: "greentree_edit",
    editLocation: (id: Guid, isEditing: boolean): Location => ({
      name: integrationsMenu.greentree.edit,
      params: {
        id: id.toString(),
        mode: isEditing ? "edit" : "view",
      },
    }),
  },
  customCsv: {
    view: "custom_csv_navigation",
    viewLocation: (): Location => ({
      name: integrationsMenu.customCsv.view,
    }),
    edit: "custom_csv_edit",
    editLocation: (id: Guid, isEditing: boolean): Location => ({
      name: integrationsMenu.customCsv.edit,
      params: {
        id: id.toString(),
        mode: isEditing ? "edit" : "view",
      },
    }),
    create: "custom_csv_create",
    createLocation: (): Location => ({
      name: integrationsMenu.customCsv.create,
    }),
  },
  eposNow: {
    view: "epos_now",
    viewLocation: (id: Guid): Location => ({
      name: integrationsMenu.eposNow.view,
      params: {
        id: id.toString(),
      },
    }),
    create: "epos_now_create",
    createLocation: (): Location => ({
      name: integrationsMenu.eposNow.create,
    }),
  },
  wooCommerce: {
    view: "woo_commerce",
    viewLocation: (id: Guid): Location => ({
      name: integrationsMenu.wooCommerce.view,
      params: {
        id: id.toString(),
      },
    }),
    create: "woo_commerce_create",
    createLocation: (): Location => ({
      name: integrationsMenu.wooCommerce.create,
    }),
  },
  ecommerce: {
    view: "ecommerce",
    viewLocation: (id: Guid): Location => ({
      name: integrationsMenu.ecommerce.view,
      params: {
        id: id.toString(),
      },
    }),
    create: "ecommerce_create",
    createLocation: (type: EnumEcommerceOrderOriginType): Location => ({
      name: integrationsMenu.ecommerce.create,
      params: {
        keyAsString: type.key.toString(),
      },
    }),
  },
  productionMonitoringTools: {
    list: "production_monitoring_tools",
  },
  lexoffice: {
    view: "lexoffice",
    viewLocation: (): Location => ({
      name: integrationsMenu.lexoffice.view,
    }),
  },
  squarespace: {
    view: "squarespace",
    viewLocation: (): Location => ({
      name: integrationsMenu.squarespace.view,
    }),
  },
  ecwid: {
    view: "ecwid",
    viewLocation: (): Location => ({
      name: integrationsMenu.ecwid.view,
    }),
  },
  maxoptra: {
    view: "maxoptra",
    viewLocation: (): Location => ({
      name: integrationsMenu.maxoptra.view,
    }),
  },
  zevero: {
    view: "zevero",
    viewLocation: (): Location => ({
      name: integrationsMenu.zevero.view,
    }),
  },
  netZeroNow: {
    view: "netZeroNow",
    viewLocation: (): Location => ({
      name: integrationsMenu.netZeroNow.view,
    }),
  },
  eebria: {
    view: "eebria",
    viewLocation: (): Location => ({
      name: integrationsMenu.eebria.view,
    }),
  },
  bigCommerce: {
    overview: "big_commerce_overview",
    viewOverview: (): Location => ({
      name: integrationsMenu.bigCommerce.overview,
    }),
    settings: "big_commerce_settings",
    viewSettings: (): Location => ({
      name: integrationsMenu.bigCommerce.settings,
    }),
    introduction: "big_commerce_introduction",
    viewIntroduction: (): Location => ({
      name: integrationsMenu.bigCommerce.introduction,
    }),
  },
  goTab: {
    view: "go_tab",
    viewLocation: (): Location => ({
      name: integrationsMenu.goTab.view,
    }),
  },
};

export const buildIntegrationsMenuItems: () => MenuItem[] = () => [
  MenuItem.fromOpts({
    name: integrationsMenu.root,
    icon: "Link",
    url: "/integrations",
    menuComponent: IntegrationsMenuItem,
    visible: () =>
      hasPermissionsUsingUserStore([EnumUserPermission.menuIntegrationsView]),
    view: (): Promise<any> =>
      import(
        /* webpackChunkName: "integrations" */ "@/components/pages/integrations/Integrations.vue"
      ),
  }),
  MenuItem.fromOpts({
    name: integrationsMenu.brewmanTradePortal.view,
    url: "/integrations/brewman_trade_portal",
    breadcrumbParentName: integrationsMenu.root,
    view: (): Promise<any> =>
      import(
        /* webpackChunkName: "integrations" */ "@/components/pages/integrations/brewmanTradePortal/BrewmanTradePortalPage.vue"
      ),
    visible: false,
  }),
  MenuItem.fromOpts({
    name: integrationsMenu.commerce7.view,
    url: "/integrations/commerce7",
    breadcrumbParentName: integrationsMenu.root,
    view: (): Promise<any> =>
      import(
        /* webpackChunkName: "integrations" */ "@/components/pages/integrations/commerce7/Commerce7Page.vue"
      ),
    visible: false,
  }),
  MenuItem.fromOpts({
    name: integrationsMenu.ptvRo.view,
    url: "/integrations/ptv_ro",
    breadcrumbParentName: integrationsMenu.root,
    view: (): Promise<any> =>
      import(
        /* webpackChunkName: "integrations" */ "@/components/pages/integrations/ptvro/PtvRoPage.vue"
      ),
    visible: false,
  }),
  MenuItem.fromOpts({
    name: integrationsMenu.wooCommerce.create,
    url: "/integrations/woocommerce/create",
    breadcrumbParentName: integrationsMenu.root,
    view: (): Promise<any> =>
      import(
        /* webpackChunkName: "integrations" */ "@/components/pages/integrations/WooCommerce.vue"
      ),
    componentProps: {
      creating: true,
    },
    visible: false,
  }),
  MenuItem.fromOpts({
    name: integrationsMenu.wooCommerce.view,
    url: "/integrations/woocommerce/view/:id",
    breadcrumbParentName: integrationsMenu.root,
    view: (): Promise<any> =>
      import(
        /* webpackChunkName: "integrations" */ "@/components/pages/integrations/WooCommerce.vue"
      ),
    visible: false,
    componentProps: (route) => ({
      idAsString: route.params.id,
    }),
  }),
  MenuItem.fromOpts({
    name: integrationsMenu.ecommerce.create,
    url: "/integrations/ecommerce/create/:keyAsString",
    breadcrumbParentName: integrationsMenu.root,
    view: (): Promise<any> =>
      import(
        /* webpackChunkName: "integrations" */ "@/components/pages/integrations/EcommerceIntegration.vue"
      ),
    componentProps: (route) => ({
      creating: true,
      keyAsString: route.params.keyAsString,
    }),
    visible: false,
  }),
  MenuItem.fromOpts({
    name: integrationsMenu.ecommerce.view,
    url: "/integrations/ecommerce/view/:id",
    breadcrumbParentName: integrationsMenu.root,
    view: (): Promise<any> =>
      import(
        /* webpackChunkName: "integrations" */ "@/components/pages/integrations/EcommerceIntegration.vue"
      ),
    visible: false,
    componentProps: (route) => ({
      idAsString: route.params.id,
    }),
  }),
  MenuItem.fromOpts({
    name: integrationsMenu.xero,
    url: "/integrations/xero",
    breadcrumbParentName: integrationsMenu.root,
    view: (): Promise<any> =>
      import(
        /* webpackChunkName: "integrations" */ "@/components/pages/integrations/Xero.vue"
      ),
    visible: false,
  }),
  MenuItem.fromOpts({
    name: integrationsMenu.fortnox,
    url: "/integrations/fortnox",
    breadcrumbParentName: integrationsMenu.root,
    view: (): Promise<any> =>
      import(
        /* webpackChunkName: "integrations" */ "@/components/pages/integrations/Fortnox.vue"
      ),
    visible: false,
  }),
  MenuItem.fromOpts({
    name: integrationsMenu.quickBooks,
    url: "/integrations/quickbooks",
    breadcrumbParentName: integrationsMenu.root,
    view: (): Promise<any> =>
      import(
        /* webpackChunkName: "integrations" */ "@/components/pages/integrations/QuickBooks.vue"
      ),
    componentProps: (route) => ({
      isSandbox: route.query.isSandbox
        ? route.query.isSandbox === "true"
        : false,
    }),
    visible: false,
  }),
  MenuItem.fromOpts({
    name: integrationsMenu.sageOne,
    url: "/integrations/sageone",
    breadcrumbParentName: integrationsMenu.root,
    view: (): Promise<any> =>
      import(
        /* webpackChunkName: "integrations" */ "@/components/pages/integrations/SageOne.vue"
      ),
    visible: false,
  }),
  MenuItem.fromOpts({
    name: integrationsMenu.sageOneZa,
    url: "/integrations/sage-one-za",
    breadcrumbParentName: integrationsMenu.root,
    view: (): Promise<any> =>
      import(
        /* webpackChunkName: "integrations" */ "@/components/pages/integrations/SageOneZa.vue"
      ),
    visible: false,
  }),
  MenuItem.fromOpts({
    name: integrationsMenu.sage50,
    url: "/integrations/sage50",
    breadcrumbParentName: integrationsMenu.root,
    view: (): Promise<any> =>
      import(
        /* webpackChunkName: "integrations" */ "@/components/pages/integrations/Sage50.vue"
      ),
    visible: false,
  }),
  MenuItem.fromOpts({
    name: integrationsMenu.sage200,
    url: "/integrations/sage200",
    breadcrumbParentName: integrationsMenu.root,
    view: (): Promise<any> =>
      import(
        /* webpackChunkName: "integrations" */ "@/components/pages/integrations/Sage200.vue"
      ),
    visible: false,
  }),
  MenuItem.fromOpts({
    name: integrationsMenu.brewmanApi,
    url: "/integrations/brewmanApi",
    breadcrumbParentName: integrationsMenu.root,
    view: (): Promise<any> =>
      import(
        /* webpackChunkName: "integrations" */ "@/components/pages/integrations/BrewmanApi.vue"
      ),
    visible: false,
  }),
  MenuItem.fromOpts({
    name: integrationsMenu.noAccountsPackage,
    url: "/integrations/noaccountspackage",
    breadcrumbParentName: integrationsMenu.root,
    view: (): Promise<any> =>
      import(
        /* webpackChunkName: "integrations" */ "@/components/pages/integrations/NoAccountsPackage.vue"
      ),
    visible: false,
  }),
  MenuItem.fromOpts({
    name: integrationsMenu.sellar.view,
    url: "/integrations/sellar/view/:id",
    breadcrumbParentName: integrationsMenu.root,
    view: (): Promise<any> =>
      import(
        /* webpackChunkName: "integrations" */ "@/components/pages/integrations/Sellar.vue"
      ),
    componentProps: (route) => ({
      settingsId: Guid.tryParse(route.params.id),
    }),
    visible: false,
  }),
  MenuItem.fromOpts({
    name: integrationsMenu.sellar.create,
    url: "/integrations/sellar/create",
    breadcrumbParentName: integrationsMenu.root,
    view: (): Promise<any> =>
      import(
        /* webpackChunkName: "integrations" */ "@/components/pages/integrations/Sellar.vue"
      ),
    visible: false,
  }),
  MenuItem.fromOpts({
    name: integrationsMenu.zettle,
    url: "/integrations/zettle",
    breadcrumbParentName: integrationsMenu.root,
    view: (): Promise<any> =>
      import(
        /* webpackChunkName: "integrations" */ "@/components/pages/integrations/Zettle.vue"
      ),
    visible: false,
  }),
  MenuItem.fromOpts({
    name: integrationsMenu.beerflex,
    url: "/integrations/beerflex",
    breadcrumbParentName: integrationsMenu.root,
    view: (): Promise<any> =>
      import(
        /* webpackChunkName: "integrations" */ "@/components/pages/integrations/Beerflex.vue"
      ),
    visible: false,
  }),
  MenuItem.fromOpts({
    name: integrationsMenu.eposNow.create,
    url: "/integrations/eposnow/create",
    breadcrumbParentName: integrationsMenu.root,
    view: (): Promise<any> =>
      import(
        /* webpackChunkName: "integrations" */ "@/components/pages/integrations/EposNow.vue"
      ),
    componentProps: {
      creating: true,
    },
    visible: false,
  }),
  MenuItem.fromOpts({
    name: integrationsMenu.eposNow.view,
    url: "/integrations/eposnow/view/:id",
    breadcrumbParentName: integrationsMenu.root,
    view: (): Promise<any> =>
      import(
        /* webpackChunkName: "integrations" */ "@/components/pages/integrations/EposNow.vue"
      ),
    visible: false,
    componentProps: (route) => ({
      idAsString: route.params.id,
    }),
  }),
  // whitelisted callback uri route used for authorisation
  MenuItem.fromOpts({
    name: integrationsMenu.square.callback,
    url: "/integrations/square/",
    breadcrumbParentName: integrationsMenu.root,
    view: (): Promise<any> =>
      import(
        /* webpackChunkName: "integrations" */ "@/components/pages/integrations/Square.vue"
      ),
    visible: false,
  }),
  MenuItem.fromOpts({
    name: integrationsMenu.square.create,
    url: "/integrations/square/create",
    breadcrumbParentName: integrationsMenu.root,
    view: (): Promise<any> =>
      import(
        /* webpackChunkName: "integrations" */ "@/components/pages/integrations/Square.vue"
      ),
    visible: false,
    componentProps: {
      creating: true,
    },
  }),
  MenuItem.fromOpts({
    name: integrationsMenu.square.view,
    url: "/integrations/square/view/:id",
    breadcrumbParentName: integrationsMenu.root,
    view: (): Promise<any> =>
      import(
        /* webpackChunkName: "integrations" */ "@/components/pages/integrations/Square.vue"
      ),
    visible: false,
    componentProps: (route) => ({
      idAsString: route.params.id,
    }),
  }),
  MenuItem.fromOpts({
    name: integrationsMenu.productionMonitoringTools.list,
    url: "/integrations/productionmonitoring/list/",
    breadcrumbParentName: integrationsMenu.root,
    view: (): Promise<any> =>
      import(
        /* webpackChunkName: "integrations" */ "@/components/pages/integrations/productionMonitoring/ListProductionMonitors.vue"
      ),
    visible: false,
  }),
  MenuItem.fromOpts({
    name: integrationsMenu.greentree.view,
    url: "/integrations/greetree/",
    breadcrumbParentName: integrationsMenu.root,
    view: (): Promise<any> =>
      import(
        /* webpackChunkName: "integrations" */ "@/components/pages/integrations/ListAccountsCsvFiles.vue"
      ),
    componentProps: () => ({
      accountsPackageType: EnumAccountsPackageType.greenTree,
      editLocation: integrationsMenu.greentree.editLocation,
    }),
    visible: false,
  }),
  MenuItem.fromOpts({
    name: integrationsMenu.greentree.edit,
    url: "/integrations/greentree/:id/:mode",
    breadcrumbParentName: integrationsMenu.greentree.view,
    view: (): Promise<any> =>
      import(
        /* webpackChunkName: "integrations" */ "@/components/pages/integrations/EditAccountsCsvFile.vue"
      ),
    componentProps: (route) => ({
      id: route.params.id,
      editing:
        route.params.id && route.params.mode.toLowerCase().trim() === "edit",
      editLocation: integrationsMenu.greentree.editLocation,
      viewLocation: integrationsMenu.greentree.viewLocation,
    }),
    visible: false,
  }),
  MenuItem.fromOpts({
    name: integrationsMenu.customCsv.view,
    url: "/integrations/customcsv/",
    breadcrumbParentName: integrationsMenu.root,
    view: (): Promise<any> =>
      import(
        /* webpackChunkName: "integrations" */ "@/components/pages/integrations/ListCustomCsvFiles.vue"
      ),
    visible: false,
  }),
  MenuItem.fromOpts({
    name: integrationsMenu.customCsv.edit,
    url: "/integrations/customcsv/:id/:mode",
    breadcrumbParentName: integrationsMenu.customCsv.view,
    view: (): Promise<any> =>
      import(
        /* webpackChunkName: "integrations" */ "@/components/pages/integrations/EditCustomCsvFile.vue"
      ),
    componentProps: (route) => ({
      id: route.params.id,
      editing:
        route.params.id && route.params.mode.toLowerCase().trim() === "edit",
    }),
    visible: false,
  }),
  MenuItem.fromOpts({
    name: integrationsMenu.customCsv.create,
    url: "/integrations/customcsv/create",
    breadcrumbParentName: integrationsMenu.customCsv.view,
    view: (): Promise<any> =>
      import(
        /* webpackChunkName: "integrations" */ "@/components/pages/integrations/EditCustomCsvFile.vue"
      ),
    componentProps: (route) => ({
      editing: true,
    }),
    visible: false,
  }),

  MenuItem.fromOpts({
    name: integrationsMenu.lexoffice.view,
    url: "/integrations/lexoffice",
    breadcrumbParentName: integrationsMenu.root,
    view: (): Promise<any> =>
      import(
        /* webpackChunkName: "integrations" */ "@/components/pages/integrations/Lexoffice.vue"
      ),
    visible: false,
  }),

  MenuItem.fromOpts({
    name: integrationsMenu.squarespace.view,
    url: "/integrations/squarespace",
    breadcrumbParentName: integrationsMenu.root,
    view: (): Promise<any> =>
      import(
        /* webpackChunkName: "integrations" */ "@/components/pages/integrations/Squarespace.vue"
      ),
    visible: false,
  }),

  MenuItem.fromOpts({
    name: integrationsMenu.ecwid.view,
    url: "/integrations/ecwid",
    breadcrumbParentName: integrationsMenu.root,
    view: (): Promise<any> =>
      import(
        /* webpackChunkName: "integrations" */ "@/components/pages/integrations/Ecwid.vue"
      ),
    visible: false,
  }),

  MenuItem.fromOpts({
    name: integrationsMenu.maxoptra.view,
    url: "/integrations/maxoptra",
    breadcrumbParentName: integrationsMenu.root,
    view: (): Promise<any> =>
      import(
        /* webpackChunkName: "integrations" */ "@/components/pages/integrations/Maxoptra.vue"
      ),
    visible: false,
  }),

  MenuItem.fromOpts({
    name: integrationsMenu.zevero.view,
    url: "/integrations/zevero",
    breadcrumbParentName: integrationsMenu.root,
    view: (): Promise<any> =>
      import(
        /* webpackChunkName: "integrations" */ "@/components/pages/integrations/Zevero.vue"
      ),
    visible: false,
  }),

  MenuItem.fromOpts({
    name: integrationsMenu.netZeroNow.view,
    url: "/integrations/net-zero-now",
    breadcrumbParentName: integrationsMenu.root,
    view: (): Promise<any> =>
      import(
        /* webpackChunkName: "integrations" */ "@/components/pages/integrations/NetZeroNow.vue"
      ),
    visible: false,
  }),

  MenuItem.fromOpts({
    name: integrationsMenu.eebria.view,
    url: "/integrations/eebria",
    breadcrumbParentName: integrationsMenu.root,
    view: (): Promise<any> =>
      import(
        /* webpackChunkName: "integrations" */ "@/components/pages/integrations/Eebria.vue"
      ),
    visible: false,
  }),
  MenuItem.fromOpts({
    name: integrationsMenu.bigCommerce.overview,
    url: "/integrations/big-commerce/",
    breadcrumbParentName: integrationsMenu.root,
    view: (): Promise<any> =>
      import(
        /* webpackChunkName: "integrations" */ "@/components/pages/integrations/newComponents/IntegrationPage.vue"
      ),
    visible: false,
    redirect: "/integrations/big-commerce/overview",
    componentProps: (route) => ({
      integrationType: EnumIntegrationType.bigCommerce,
      pageTitle: messages.big_commerce_title,
      tabItems: bigCommerceTabs(),
    }),
    childRoutes: [
      {
        name: integrationsMenu.bigCommerce.overview,
        path: "overview",
        component: () =>
          import(
            /* webpackChunkName: "integrations" */ "@/components/pages/integrations/bigCommerce/BigCommerceOverviewTab.vue"
          ),
        meta: {
          key: "/integrations/big-commerce/overview",
        },
      },
      {
        name: integrationsMenu.bigCommerce.settings,
        path: "settings",
        component: () =>
          import(
            /* webpackChunkName: "integrations" */ "@/components/pages/integrations/bigCommerce/BigCommerceSettingsTab.vue"
          ),
        meta: {
          key: "/integrations/big-commerce/settings",
        },
      },
    ],
  }),
  MenuItem.fromOpts({
    name: integrationsMenu.bigCommerce.introduction,
    url: "/integrations/big-commerce/introduction",
    breadcrumbParentName: integrationsMenu.root,
    view: (): Promise<any> =>
      import(
        /* webpackChunkName: "integrations" */ "@/components/pages/integrations/bigCommerce/BigCommerceSettingsPage.vue"
      ),
    visible: false,
  }),
  MenuItem.fromOpts({
    name: integrationsMenu.goTab.view,
    url: "/integrations/gotab",
    breadcrumbParentName: integrationsMenu.root,
    view: (): Promise<any> =>
      import(
        /* webpackChunkName: "integrations" */ "@/components/pages/integrations/goTab/GoTabSettingsPage.vue"
      ),
    visible: false,
  }),
  MenuItem.forRoute("", integrationRoutes.whatsapp, { visible: false }),
];
